// @flow

import React from 'react';
import { camelizeKeys } from 'humps';
import {
  Button,
  Modal,
  ModalHeader,
  Input,
  InputGroup,
  InputGroupAddon,
} from 'reactstrap';

import api from '../../lib/api';
import { useToggle } from '../../hooks';
import endpoints from '../../lib/endpoints';

type PromoCodeResponse = {
  promocode: string,
  promocodeAmount: string,
  purchaseAmount: string,
  totalAmount: string,
  paymentGateway: string,
  transactionFeeName: string,
};

type Props = {
  invoiceId: string,
  onSuccess: (promocodeResponse: PromoCodeResponse) => void,
  onDelete: () => void,
  isPromocodeApplied: boolean,
};

export default function PromoCodeInput(props: Props) {
  const { onSuccess, onDelete, isPromocodeApplied, invoiceId, transactionFeeName, paymentGateway } = props;

  const { on, toggle } = useToggle();
  const [promocode, setPromocode] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  function handlePromocode(e) {
    setPromocode(e.target.value);
    if (error) setError(null);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setIsLoading(true);

    try {
      const promocodeResponse: PromoCodeResponse = await verify(promocode, Number(invoiceId), transactionFeeName, paymentGateway);
      toggle();
      onSuccess(promocodeResponse);
    } catch (err) {
      setError(err.response.data.message);
    } finally {
      setIsLoading(false);
    }
  }

  if (isPromocodeApplied) {
    return <CancelPromocodeButton promocode={promocode} onDelete={onDelete} />;
  }

  return (
    <React.Fragment>
      <span onClick={toggle} className="promo-ent" id="flights-enter-HG-credit-btn">Enter HoneyGuide Credits</span>
      <Modal isOpen={on} toggle={toggle} className="flight_modal">
        <ModalHeader toggle={toggle}>Enter HoneyGuide Credits</ModalHeader>
        <form onSubmit={handleSubmit}>
          <InputGroup>
            <Input
              type="text"
              value={promocode}
              onChange={handlePromocode}
              aria-label="Enter HoneyGuide Credits"
            />
            <InputGroupAddon addonType="append" className="promo">
              <Button
                color="yellow"
                className={isLoading ? 'btn-loading' : ''}
                onClick={handleSubmit}
                disabled={!promocode}
                id="flights-apply-HG-credit-button"
              >
                {isLoading ? 'APPLYING CREDIT' : 'APPLY CREDIT'}
              </Button>
            </InputGroupAddon>
          </InputGroup>
          {error && <p className="error">{error}</p>}
        </form>
      </Modal>
    </React.Fragment>
  );
}

type CancelPromocodeButtonProps = {
  promocode: string,
  onDelete: () => void,
};

function CancelPromocodeButton(props: CancelPromocodeButtonProps) {
  const { promocode, onDelete } = props;

  return (
    <React.Fragment>
      <span>
        HoneyGuide Credits <strong>{promocode}</strong> applied
      </span>
      <Button color="primary" className="outline" size="sm" onClick={onDelete}>
        Cancel HoneyGuide Credits
      </Button>
    </React.Fragment>
  );
}

async function verify(promocode: string, invoice_id: number, transactionFeeName: string, paymentGateway: string) {
    const { data } = await api.postWithoutToken(endpoints.payment.verify, {
      invoice_id,
      promocode: promocode.trim(),
      additional_costs: {
				payment_gateway: paymentGateway,
			}
    });

    const camelizedData = camelizeKeys(data); 
    const { purchaseAmount, totalAmount } = camelizedData;

    const { amount: promocodeAmount  } = camelizedData.data.reduce((acc, obj) => {
      if (obj.name === 'promocode') {
        acc = obj;
      };
      return acc;
    }, null);

    const additionalCost = camelizedData.data.reduce((acc, obj) => {
      if (obj.name === transactionFeeName) {
        acc = obj.amount;
      };
      return acc;
    }, null);

    return {
      promocode,
      promocodeAmount,
      purchaseAmount,
      totalAmount,
      additionalCost,
    };
}



// WEBPACK FOOTER //
// ./src/components/Promocode/Promocode.js